import React, { useState, useMemo } from 'react';
import { 
  Sparkles, 
  CheckCircle2, 
  AlertCircle, 
  Search, 
  Flame, 
  FlaskConical, 
  Cog, 
  Layers, 
  Clock, 
  FileCheck,
  TrendingUp,
  Award
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  LabelList,
  ReferenceLine 
} from 'recharts';
import { Folio } from '../../types.ts';
import { PeriodDataPoint } from '../KpisManager.tsx';

interface CleaningEfficiencyReportViewProps {
  folios: Folio[];
  periodDataList: PeriodDataPoint[];
  periodMode: 'DIAS' | 'SEMANAS' | 'MESES';
  clientName?: string | null;
}

export default function CleaningEfficiencyReportView({
  folios,
  periodDataList,
  periodMode,
  clientName
}: CleaningEfficiencyReportViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [processFilter, setProcessFilter] = useState<'ALL' | 'TERMICO' | 'QUIMICO' | 'GRANALLADORA'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Process and analyze cleaning/wash performance for each folio
  const cleaningRecords = useMemo(() => {
    return folios.map(f => {
      const totalPieces = f.items?.reduce((s, it) => s + (Number(it.quantity) || 0), 0) || 0;
      
      const washStage = f.history?.find(h => h.stage === 'DECAPADO_LAVADO');
      const hasWash = Boolean(washStage) || (!f.hasGranallado && f.history?.some(h => ['INSPECCION', 'ENTREGA_PLANTA'].includes(h.stage)));
      
      const reworkStage = f.history?.find(h => h.stage === 'RETRABAJO');
      const hasCleaningIssue = reworkStage && (
        reworkStage.incidenceReport?.toLowerCase().includes('limpieza') ||
        reworkStage.incidenceReport?.toLowerCase().includes('ceniza') ||
        reworkStage.incidenceReport?.toLowerCase().includes('hollín') ||
        reworkStage.incidenceReport?.toLowerCase().includes('grasa') ||
        reworkStage.notes?.toLowerCase().includes('limpieza')
      );

      const cleaningEfficiency = hasCleaningIssue ? 85 : 100;
      const isApprovedFirstPass = !hasCleaningIssue;

      // Detect process type
      let processTypeLabel = 'Decapado Térmico';
      let processCategory: 'TERMICO' | 'QUIMICO' | 'GRANALLADORA' = 'TERMICO';

      if (f.hasGranallado || f.processType === 'DECAPADO_GRANALLADORA') {
        processTypeLabel = 'Granalladora Mecánica';
        processCategory = 'GRANALLADORA';
      } else if (f.processType === 'DECAPADO_QUIMICO') {
        processTypeLabel = 'Decapado Químico';
        processCategory = 'QUIMICO';
      }

      return {
        id: f.id,
        folioNumber: (f as any).folioNumber || f.id,
        date: washStage?.timestamp || f.createdDate || '',
        clientName: f.clientName || 'Cliente',
        costCenterName: f.costCenterName || 'Planta',
        totalPieces,
        hasWash,
        processTypeLabel,
        processCategory,
        isApprovedFirstPass,
        cleaningEfficiency,
        notes: washStage?.notes || (isApprovedFirstPass ? 'Lavado y desengrase conforme' : 'Requiere re-lavado por ceniza residual'),
        operator: washStage?.operatorName || f.createdBy || 'Operador Lavado',
        status: f.status
      };
    });
  }, [folios]);

  // Overall Cleaning KPIs calculated over devoluciones: total piezas entregadas vs devoluciones
  const metrics = useMemo(() => {
    let totalPiecesDelivered = 0;
    let totalDevoluciones = 0;
    let countTermico = 0;
    let countQuimico = 0;
    let countGranalladora = 0;

    periodDataList.forEach(p => {
      totalPiecesDelivered += p.piecesDelivered;
      totalDevoluciones += p.devolucionPieces;
    });

    cleaningRecords.forEach(r => {
      if (r.processCategory === 'TERMICO') countTermico += r.totalPieces;
      else if (r.processCategory === 'QUIMICO') countQuimico += r.totalPieces;
      else countGranalladora += r.totalPieces;
    });

    // Eficiencia calculada sobre devoluciones: (totalDelivered - devoluciones) / totalDelivered * 100
    const efficiencyPercent = totalPiecesDelivered > 0 
      ? Math.min(100, Math.max(0, ((totalPiecesDelivered - Math.min(totalDevoluciones, totalPiecesDelivered)) / totalPiecesDelivered) * 100))
      : 100;

    const complianceGoal = 95.0; // Institutional Cleaning Goal: 95%

    return {
      totalPiecesDelivered,
      totalDevoluciones,
      totalPiecesWashed: totalPiecesDelivered,
      approvedFirstPassPieces: Math.max(0, totalPiecesDelivered - totalDevoluciones),
      efficiencyPercent: Number(efficiencyPercent.toFixed(1)),
      complianceGoal,
      countTermico,
      countQuimico,
      countGranalladora
    };
  }, [periodDataList, cleaningRecords]);

  // Chart Data: Cleaning Efficiency measured in PERCENTAGE per Period calculated over devoluciones
  const chartData = useMemo(() => {
    return periodDataList.map(p => {
      const delivered = p.piecesDelivered;
      const devoluciones = p.devolucionPieces;
      // Eficiencia de limpieza calculada sobre devoluciones
      const efficiency = delivered > 0
        ? Math.min(100, Math.max(0, ((delivered - Math.min(devoluciones, delivered)) / delivered) * 100))
        : 100;

      return {
        periodLabel: p.periodLabel,
        piecesDelivered: delivered,
        devolucionPieces: devoluciones,
        eficienciaLimpieza: Number(efficiency.toFixed(1)),
        metaPercent: 95.0
      };
    });
  }, [periodDataList]);

  // Filtered Table
  const filteredList = useMemo(() => {
    let list = cleaningRecords;
    if (processFilter !== 'ALL') {
      list = list.filter(r => r.processCategory === processFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(r => 
        r.folioNumber.toLowerCase().includes(q) ||
        r.clientName.toLowerCase().includes(q) ||
        r.notes.toLowerCase().includes(q) ||
        r.operator.toLowerCase().includes(q)
      );
    }
    return list;
  }, [cleaningRecords, processFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / rowsPerPage));
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredList.slice(start, start + rowsPerPage);
  }, [filteredList, currentPage]);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* SUMMARY KPI CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* 1. Eficiencia de Limpieza */}
        <div className={`bg-[#242624] border rounded-2xl p-4 shadow-md ${
          metrics.efficiencyPercent >= metrics.complianceGoal 
            ? 'border-cyan-500/40 bg-gradient-to-b from-cyan-950/25 to-transparent' 
            : 'border-yellow-500/40 bg-gradient-to-b from-yellow-950/20 to-transparent'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Eficiencia de Limpieza</span>
            <Sparkles className="h-4 w-4 text-cyan-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono flex items-baseline gap-2">
            <span className="text-cyan-400">{metrics.efficiencyPercent.toFixed(1)}%</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
              metrics.efficiencyPercent >= 95.0
                ? 'bg-emerald-950/60 text-emerald-400 border-emerald-700'
                : 'bg-red-950/60 text-red-400 border-red-700'
            }`}>
              Meta 95%
            </span>
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            {metrics.efficiencyPercent >= 95.0 ? 'Cumple meta institucional ≥ 95%' : 'Por debajo de la meta del 95%'}
          </div>
        </div>

        {/* 2. Total Piezas Entregadas / Procesadas */}
        <div className="bg-[#242624] border border-[#3a3d3a] rounded-2xl p-4 shadow-md bg-gradient-to-b from-blue-950/20 to-transparent">
          <div className="flex items-center justify-between text-blue-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300">Total Piezas Entregadas</span>
            <Layers className="h-4 w-4 text-blue-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono">
            {metrics.totalPiecesDelivered.toLocaleString()}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Base total para el cálculo
          </div>
        </div>

        {/* 3. Devoluciones */}
        <div className="bg-[#242624] border border-red-500/40 rounded-2xl p-4 shadow-md bg-gradient-to-b from-red-950/20 to-transparent">
          <div className="flex items-center justify-between text-red-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-300">Devoluciones Reales</span>
            <CheckCircle2 className="h-4 w-4 text-red-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-red-400 font-mono">
            {metrics.totalDevoluciones.toLocaleString()}
          </div>
          <div className="text-[10px] text-red-300/80 mt-1 truncate">
            Piezas devueltas por el cliente
          </div>
        </div>

        {/* 4. Estándar de Cumplimiento Meta 95% */}
        <div className="bg-[#242624] border border-emerald-500/40 rounded-2xl p-4 shadow-md bg-gradient-to-b from-emerald-950/20 to-transparent">
          <div className="flex items-center justify-between text-emerald-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">Meta Institucional</span>
            <Award className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-emerald-400 font-mono">
            ≥ 95.0%
          </div>
          <div className="text-[10px] text-emerald-300/80 mt-1 truncate">
            {metrics.efficiencyPercent >= 95.0 ? 'Estatus: CUMPLE META' : 'Estatus: EN SEGUIMIENTO'}
          </div>
        </div>
      </div>

      {/* CHART & PROCESS DISTRIBUTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Chart */}
        <div className="lg:col-span-2 bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-[#343634]">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-cyan-400" />
                <span>Porcentaje de Eficiencia de Limpieza (% Eficiencia)</span>
              </h3>
              <p className="text-xs text-slate-400">
                Calculado sobre las devoluciones respecto a la cantidad total. Meta institucional: ≥ 95.0%.
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-xs bg-cyan-400" />
                <span className="text-cyan-300 font-bold">% Eficiencia</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-0.5 w-4 border-t-2 border-dashed border-emerald-400" />
                <span className="text-emerald-400 font-bold">Meta 95%</span>
              </div>
            </div>
          </div>

          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 25, right: 25, left: -10, bottom: 15 }}>
                <defs>
                  <linearGradient id="cleaningEfficiencyBarGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#0891b2" stopOpacity={0.75} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#343634" opacity={0.6} />
                <XAxis dataKey="periodLabel" stroke="#8e918e" fontSize={11} tickLine={false} dy={6} />
                
                {/* Single Y Axis measured in PERCENTAGE */}
                <YAxis 
                  stroke="#8e918e" 
                  fontSize={11} 
                  tickLine={false} 
                  domain={[0, 105]} 
                  tickFormatter={v => `${v}%`} 
                />

                {/* Institutional Meta Line at 95% */}
                <ReferenceLine 
                  y={95.0} 
                  stroke="#10b981" 
                  strokeWidth={2} 
                  strokeDasharray="4 4" 
                  label={{ 
                    value: 'META 95%', 
                    position: 'insideTopRight', 
                    fill: '#10b981', 
                    fontSize: 11, 
                    fontWeight: 'bold' 
                  }} 
                />

                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload;
                      return (
                        <div className="bg-[#181918] border border-[#3e413e] rounded-xl p-3 shadow-2xl space-y-1.5 text-xs">
                          <div className="flex items-center justify-between gap-4 border-b border-[#303330] pb-1">
                            <span className="font-bold text-white">{label}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              d.eficienciaLimpieza >= 95.0 
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>
                              {d.eficienciaLimpieza >= 95.0 ? 'CUMPLE META ≥ 95%' : 'POR DEBAJO DE META'}
                            </span>
                          </div>
                          <div className="text-cyan-400 pt-1 flex justify-between gap-3 font-bold text-sm">
                            <span>% Eficiencia de Limpieza:</span>
                            <span className="font-mono">{d.eficienciaLimpieza.toFixed(1)}%</span>
                          </div>
                          <div className="text-[11px] text-slate-400 pt-1 border-t border-[#2d2f2d] flex justify-between gap-3">
                            <span>Total Facturado / Entregado:</span>
                            <span className="font-mono text-white">{d.piecesDelivered.toLocaleString()} pzas</span>
                          </div>
                          <div className="text-[11px] text-slate-400 flex justify-between gap-3">
                            <span>Devoluciones:</span>
                            <span className="font-mono text-white">{d.devolucionPieces.toLocaleString()} pzas</span>
                          </div>
                          <div className="text-[11px] text-emerald-400 flex justify-between gap-3">
                            <span>Meta Institucional:</span>
                            <span className="font-mono font-bold">≥ 95.0%</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />

                <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 10, fontSize: 11 }} />

                {/* The Bar measured in PERCENTAGE with Data Labels */}
                <Bar 
                  dataKey="eficienciaLimpieza" 
                  name="% Eficiencia de Limpieza" 
                  fill="url(#cleaningEfficiencyBarGradient)" 
                  radius={[6, 6, 0, 0]} 
                  maxBarSize={48}
                >
                  <LabelList 
                    dataKey="eficienciaLimpieza" 
                    position="top" 
                    offset={8} 
                    formatter={(val: any) => `${Number(val).toFixed(1)}%`} 
                    style={{ fill: '#22d3ee', fontSize: 11, fontWeight: 'bold' }} 
                  />
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Col: Process Type Distribution */}
        <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
          <div className="pb-2 border-b border-[#343634]">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-purple-400" />
              <span>Métodos de Limpieza / Decapado</span>
            </h3>
            <p className="text-xs text-slate-400">
              Distribución de volumen por tecnología aplicada.
            </p>
          </div>

          <div className="space-y-4 pt-2">
            {/* Térmico */}
            <div className="space-y-1.5 bg-[#171817] p-3 rounded-xl border border-[#343634]">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-orange-400 flex items-center gap-1.5">
                  <Flame className="h-4 w-4" /> Decapado Térmico + Lavado
                </span>
                <span className="font-mono text-white font-bold">{metrics.countTermico.toLocaleString()} pzs</span>
              </div>
              <div className="text-[10px] text-slate-400">Piro-limpieza controlada y lavado con enjuague a presión</div>
            </div>

            {/* Químico */}
            <div className="space-y-1.5 bg-[#171817] p-3 rounded-xl border border-[#343634]">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-purple-400 flex items-center gap-1.5">
                  <FlaskConical className="h-4 w-4" /> Decapado Químico
                </span>
                <span className="font-mono text-white font-bold">{metrics.countQuimico.toLocaleString()} pzs</span>
              </div>
              <div className="text-[10px] text-slate-400">Inmersión en tinas de desengrase y decapado de alta eficiencia</div>
            </div>

            {/* Granalladora */}
            <div className="space-y-1.5 bg-[#171817] p-3 rounded-xl border border-[#343634]">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-cyan-400 flex items-center gap-1.5">
                  <Cog className="h-4 w-4" /> Granalladora Mecánica
                </span>
                <span className="font-mono text-white font-bold">{metrics.countGranalladora.toLocaleString()} pzs</span>
              </div>
              <div className="text-[10px] text-slate-400">Limpieza por impacto de micro-granalla de acero</div>
            </div>
          </div>
        </div>
      </div>

      {/* DETAILED CLEANING BATCH TABLE */}
      <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-[#343634]">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-cyan-400" />
              <span>Bitácora de Lotes de Limpieza y Desengrase</span>
            </h3>
            <p className="text-xs text-slate-400">
              Control de calidad en etapas de DECAPADO_LAVADO e inspección posterior.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Buscar lote..."
                className="bg-[#161716] border border-[#3e413e] rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <select
              value={processFilter}
              onChange={e => { setProcessFilter(e.target.value as any); setCurrentPage(1); }}
              className="bg-[#161716] border border-[#3e413e] rounded-xl px-3 py-1.5 text-xs text-white focus:border-cyan-500 focus:outline-none cursor-pointer"
            >
              <option value="ALL">Todas las tecnologías</option>
              <option value="TERMICO">Térmico</option>
              <option value="QUIMICO">Químico</option>
              <option value="GRANALLADORA">Granalladora</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-[#3a3d3a] text-slate-400 uppercase tracking-wider text-[11px] bg-[#1a1c1a]">
                <th className="px-3.5 py-3">Folio / Lote</th>
                <th className="px-3 py-3">Cliente</th>
                <th className="px-3 py-3">Método Aplicado</th>
                <th className="px-3 py-3 text-center">Piezas</th>
                <th className="px-3 py-3 text-center">Dictamen Limpieza</th>
                <th className="px-3.5 py-3">Observaciones Técnicas</th>
                <th className="px-3 py-3 text-center">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2d2a]">
              {paginatedList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    No se encontraron registros de limpieza para los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                paginatedList.map(item => (
                  <tr key={item.id} className="hover:bg-[#242624] transition-colors">
                    <td className="px-3.5 py-3 font-mono font-bold text-white whitespace-nowrap">
                      {item.folioNumber}
                    </td>
                    <td className="px-3 py-3 text-slate-300">
                      {item.clientName}
                    </td>
                    <td className="px-3 py-3 text-slate-300">
                      <span className="font-medium text-slate-200">{item.processTypeLabel}</span>
                    </td>
                    <td className="px-3 py-3 text-center font-mono font-bold text-blue-400">
                      {item.totalPieces.toLocaleString()} pzs
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        item.isApprovedFirstPass 
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' 
                          : 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                      }`}>
                        {item.isApprovedFirstPass ? '100% Conforme' : 'Re-Lavado'}
                      </span>
                    </td>
                    <td className="px-3.5 py-3 text-slate-300 max-w-xs truncate">
                      {item.notes}
                    </td>
                    <td className="px-3 py-3 text-center text-slate-400 whitespace-nowrap font-mono">
                      {item.date ? new Date(item.date).toLocaleDateString('es-MX') : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-slate-400">
          <div>
            Mostrando <span className="font-bold text-white">{filteredList.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}</span> a <span className="font-bold text-white">{Math.min(currentPage * rowsPerPage, filteredList.length)}</span> de <span className="font-bold text-white">{filteredList.length}</span> registros
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Anterior
            </button>
            <span className="px-2 font-mono">Pág. {currentPage} de {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
